import React, { useState, useEffect, useMemo } from "react";
import {
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import styled from "styled-components";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";

const HeaderWrapper = styled.header`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  transition: all 0.3s ease-in-out;
  background: transparent;

  &.scrolled {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const StyledToolbar = styled(Toolbar)`
  && {
    display: flex;
    justify-content: space-between;
    padding: 0 24px;
    height: ${(props) => (props.scrolled ? "70px" : "80px")};
    transition: all 0.3s ease-in-out;

    @media (max-width: 1200px) {
      padding: 0 20px;
      height: ${(props) => (props.scrolled ? "65px" : "75px")};
    }

    @media (max-width: 1024px) {
      padding: 0 16px;
      height: ${(props) => (props.scrolled ? "60px" : "70px")};
    }

    @media (max-width: 768px) {
      padding: 0 12px;
      height: ${(props) => (props.scrolled ? "55px" : "65px")};
    }

    @media (max-width: 480px) {
      padding: 0 8px;
      height: ${(props) => (props.scrolled ? "50px" : "60px")};
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;

  @media (max-width: 1024px) {
    margin-left: 0;
  }

  @media (max-width: 768px) {
    padding: 4px 0;
  }

  @media (max-width: 480px) {
    padding: 2px 0;
  }
`;

const Logo = styled.img`
  height: ${(props) => (props.scrolled ? "40px" : "45px")};
  width: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  filter: ${(props) => !props.scrolled && "brightness(0) invert(1)"};

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 1024px) {
    height: ${(props) => (props.scrolled ? "35px" : "40px")};
  }

  @media (max-width: 768px) {
    height: ${(props) => (props.scrolled ? "30px" : "35px")};
  }

  @media (max-width: 480px) {
    height: ${(props) => (props.scrolled ? "25px" : "30px")};
  }
`;

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 1200px) {
    gap: 4px;
  }

  @media (max-width: 1080px) {
    display: none;
  }
`;

const NavButton = styled(Button)`
  && {
    margin: 0 8px;
    padding: 6px 16px;
    color: ${(props) => (props.scrolled ? "#1a2b6d" : "white")};
    font-weight: 500;
    position: relative;
    transition: all 0.3s ease;
    font-size: 1rem;
    text-shadow: ${(props) =>
      !props.scrolled && "0 1px 2px rgba(0, 0, 0, 0.1)"};

    @media (max-width: 1200px) {
      margin: 0 6px;
      padding: 6px 12px;
      font-size: 0.95rem;
    }

    @media (max-width: 1024px) {
      margin: 0 4px;
      padding: 6px 10px;
      font-size: 0.9rem;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: ${(props) => (props.scrolled ? "#1a2b6d" : "white")};
      transform: scaleX(0);
      transition: transform 0.3s ease;
    }

    &.active {
      color: ${(props) => (props.scrolled ? "#1a2b6d" : "white")};
      font-weight: 600;

      &::after {
        transform: scaleX(1);
      }
    }

    &:hover {
      background: ${(props) => !props.scrolled && "rgba(255, 255, 255, 0.1)"};
    }
  }
`;

const SignUpButton = styled(Button)`
  && {
    background: ${(props) =>
      props.scrolled
        ? "linear-gradient(135deg, #1a2b6d 0%, #529dff 100%)"
        : "rgba(255, 255, 255, 0.15)"};
    color: ${(props) => (props.scrolled ? "white" : "white")};
    border: ${(props) =>
      !props.scrolled && "1px solid rgba(255, 255, 255, 0.3)"};
    padding: 10px 24px;
    font-size: 1rem;
    border-radius: 30px;
    text-transform: capitalize;
    font-weight: 600;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    backdrop-filter: ${(props) => !props.scrolled && "blur(5px)"};

    &:hover {
      background: ${(props) =>
        props.scrolled
          ? "linear-gradient(135deg, #1a2b6d 0%, #529dff 100%)"
          : "rgba(255, 255, 255, 0.25)"};
      transform: translateY(-1px);
      box-shadow: ${(props) =>
        props.scrolled
          ? "0 4px 12px rgba(26, 43, 109, 0.2)"
          : "0 4px 12px rgba(255, 255, 255, 0.1)"};
    }

    @media (max-width: 1200px) {
      padding: 8px 20px;
      font-size: 0.95rem;
    }

    @media (max-width: 1080px) {
      display: none;
    }
  }
`;

const MobileMenuButton = styled(IconButton)`
  && {
    display: none;
    margin-left: auto;
    color: ${(props) => (props.scrolled ? "#1a2b6d" : "white")};
    padding: 8px;

    @media (max-width: 1080px) {
      display: flex;
    }

    @media (max-width: 480px) {
      padding: 6px;
    }
  }
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 280px;
    background: white;
    padding: 20px;

    @media (max-width: 480px) {
      width: 250px;
      padding: 16px;
    }
  }
`;

const DrawerList = styled(List)`
  && {
    margin-top: 20px;

    @media (max-width: 480px) {
      margin-top: 16px;
    }
  }
`;

const DrawerItem = styled(ListItem)`
  && {
    padding: 12px;
    margin-bottom: 8px;
    border-radius: 12px;
    transition: all 0.3s ease;
    color: #1a2b6d;
    font-size: 1rem;

    @media (max-width: 480px) {
      padding: 10px;
      margin-bottom: 6px;
      font-size: 0.95rem;
    }

    &:hover {
      background: rgba(26, 43, 109, 0.1);
    }

    &.active {
      background: rgba(26, 43, 109, 0.1);
      font-weight: 600;
    }
  }
`;

const SpecialDrawerItem = styled(DrawerItem)`
  && {
    background: linear-gradient(135deg, #1a2b6d 0%, #529dff 100%);
    color: white;
    border-radius: 30px;
    margin-top: 16px;
    padding: 14px 20px;
    font-weight: 600;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #1a2b6d 0%, #529dff 100%);
      opacity: 0.9;
      transform: translateY(-1px);
    }

    @media (max-width: 480px) {
      padding: 12px 16px;
    }
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pageScrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The tracking page sits on a light background, where the transparent
  // white-text navbar would be unreadable — keep it in its solid state there.
  const scrolled = pageScrolled || pathname === "/track-order";

  const sections = useMemo(
    () => [
      { id: "features", label: "Why Us?" },
      { id: "personnel", label: "About" },
      { id: "workflow", label: "Workflow" },
      { id: "sourcing", label: "Sourcing" },
      { id: "shipping", label: "Shipping" },
      { id: "contact", label: "Contact" },
    ],
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);

      // Clear active section if at the top of the page
      if (scrollPosition < 50) {
        setActiveSection("");
        return;
      }

      // Find the current active section
      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: document.getElementById(section.id),
      }));

      // Get all visible sections
      const visibleSections = sectionElements.filter((section) => {
        if (!section.element) return false;
        const rect = section.element.getBoundingClientRect();
        const buffer = 100; // Buffer zone of 100px
        return rect.top <= buffer && rect.bottom >= buffer;
      });

      // If we have visible sections, set the first one as active
      // If no sections are visible, clear the active state
      if (visibleSections.length > 0) {
        setActiveSection(visibleSections[0].id);
      } else {
        setActiveSection("");
      }
    };

    // Initial check
    handleScroll();

    // Add scroll listener with throttling to improve performance
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", scrollListener);
    return () => window.removeEventListener("scroll", scrollListener);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    // special case for track order
    if (sectionId === "track-order") {
      navigate("/track-order");
      return;
    }

    // navigate to "/" route and scroll to the section
    navigate("/");
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80; // Height of the navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      setActiveSection(sectionId);
      setIsOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <HeaderWrapper className={scrolled ? "scrolled" : ""}>
      <StyledToolbar scrolled={scrolled}>
        <LogoContainer>
          <Link to="/">
            <Logo
              src="/images/ds-og.svg"
              alt="Dropship India Logo"
              scrolled={scrolled}
            />
          </Link>
        </LogoContainer>
        <NavContainer>
          {sections.map((section) => (
            <NavButton
              key={section.id}
              className={activeSection === section.id ? "active" : ""}
              onClick={() => scrollToSection(section.id)}
              scrolled={scrolled}
            >
              {section.label}
            </NavButton>
          ))}

          <NavButton
            onClick={() => scrollToSection("track-order")}
            scrolled={scrolled}
          >
            Track Order
          </NavButton>

          <SignUpButton
            onClick={() => window.open("https://dropshipindia.live/", "_blank")}
            scrolled={scrolled}
          >
            Get Started
          </SignUpButton>
        </NavContainer>
        <MobileMenuButton
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          scrolled={scrolled}
        >
          <MenuIcon />
        </MobileMenuButton>
      </StyledToolbar>

      <StyledDrawer
        anchor="right"
        open={isOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <DrawerList>
          {sections.map((section) => (
            <DrawerItem
              key={section.id}
              button
              onClick={() => scrollToSection(section.id)}
              className={activeSection === section.id ? "active" : ""}
            >
              {section.label}
            </DrawerItem>
          ))}
          <SpecialDrawerItem
            button
            onClick={() => {
              window.open("https://dropshipindia.live/", "_blank");
              handleDrawerToggle();
            }}
          >
            Get Started
          </SpecialDrawerItem>
        </DrawerList>
      </StyledDrawer>
    </HeaderWrapper>
  );
};

export default Navbar;
